/**
 * jrs-v2-customizer-success-step.js — terminal "success" step (post-add receipt)
 *
 * @version 0.1.0 (2026-06-05) — Stage 2 skeleton: registers the terminal
 *   'success' step, accepts a frozen display snapshot via prepare(), renders a
 *   placeholder single-product receipt, and wires the two CTAs + close
 *   semantics. The full Option-2 receipt + cart-meta + bundle render land in
 *   Stages 3–4.
 *
 * Architecture (Doc 6 §2 registration; §13.4 close exception):
 *   - Registers via JRS.CustomizerPopup.registerStep('success', { onEnter,
 *     onExit, onDestroy, terminal: true }). `terminal:true` tells the shell to
 *     hide the progress bar / step label / back buttons and to treat every
 *     close from here as a converted close (clears the draft — decision 4).
 *   - The review controller, on a successful PDP add, calls
 *     JRS.CustomizerSuccessStep.prepare(snapshot) then goToStep('success'). The
 *     snapshot is a FROZEN plain object captured at confirm time and held here
 *     (not in state.draft) so a post-confirm RESET cannot blank the receipt
 *     (FR3). onEnter renders from it.
 *   - CTAs are the sanctioned exception to "controllers don't call close()"
 *     (Doc 6 §13.4): "Continue shopping" closes; "View cart" clears the draft
 *     and navigates. All exits suppress the abandon event + save-on-close
 *     prompt (the shell's terminal-close handling does this).
 *
 * Cart-edit mode never reaches this step — the review controller only routes
 * here for PDP add-to-cart (FR2 / FR7).
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'success';

  /** Authoritative cart-meta read timeout (decision 2 / D3). */
  var CART_META_RETRY_MS = 800;

  /** @type {Object|null} Frozen display snapshot set by prepare() at confirm. */
  var _snapshot = null;
  /** @type {HTMLElement|null} */
  var _mountEl = null;
  /** Bound listeners for cleanup in onExit. */
  var _bound = [];
  /**
   * One-action guard (FR9 double-fire). Once a success_action — view_cart /
   * continue_shopping / dismissed — is taken, further CTA clicks, repeated ESC,
   * or the close-driven onExit are ignored, so the event + any navigation fire
   * exactly once.
   */
  var _actionFired = false;

  /** Logo-breakdown collapse state (sixth amendment) — collapsed by default,
      reset per receipt session. */
  var _breakdownExpanded = false;
  var _hasBreakdownRows = false;

  function _onBreakdownToggle(e) {
    if (e) e.preventDefault();
    if (!_mountEl || !_hasBreakdownRows) return;
    _breakdownExpanded = !_breakdownExpanded;
    var btn = _mountEl.querySelector('[data-jrs-success-breakdown-toggle]');
    var bd = _mountEl.querySelector('[data-jrs-success-logo-breakdown]');
    if (btn) btn.setAttribute('aria-expanded', _breakdownExpanded ? 'true' : 'false');
    var aff = _mountEl.querySelector('[data-jrs-success-breakdown-affordance]');
    if (aff) aff.textContent = _breakdownExpanded ? 'Hide breakdown' : 'See breakdown';
    if (bd) {
      if (_breakdownExpanded) bd.removeAttribute('hidden');
      else bd.setAttribute('hidden', '');
    }
  }

  // ---------------------------------------------------------------------------
  // Snapshot handoff (review → success)
  // ---------------------------------------------------------------------------

  /**
   * Store the frozen display snapshot the success step renders from. Called by
   * the review controller immediately before goToStep('success'). FR3 — do not
   * re-read live config state after this; render only from the snapshot.
   * @param {Object} snapshot
   */
  function prepare(snapshot) {
    _snapshot = (snapshot && typeof snapshot === 'object') ? snapshot : null;
    _actionFired = false;
    _breakdownExpanded = false; // fresh receipt opens collapsed
    _hasBreakdownRows = false;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  function onEnter(state, context) {
    _mountEl = context && context.mountEl;
    if (!_mountEl) return;
    _actionFired = false; // fresh terminal session (defensive; prepare() also resets)

    // No persistent right-rail summary on the terminal receipt.
    if (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.dispatch === 'function') {
      JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });
    }
    // The reassurance proof line (FR4) is rendered IN-BODY in the success footer
    // (matching the Option-2 mockup, where .reassure lives inside .sheet-footer).
    // We intentionally do NOT call the chrome setReassurance() — the shell hides
    // the chrome footer on terminal steps, so the success step owns its footer.

    _render(_snapshot);
    _bindInteractions();
    _focusHeading();
    // D3 / decision 2 — authoritative cart-meta read AFTER the add. Async +
    // non-blocking so the receipt paints instantly; the row fills in (or stays
    // hidden) when the single getCart() resolves/fails. Guarded against a close
    // mid-flight by mount identity.
    _loadCartMeta();
  }

  function onExit() {
    // FR9 — leaving the terminal step without a CTA having fired (X / scrim /
    // ESC / swipe) is a 'dismissed' success_action. onExit fires on the
    // success→close transition; the CTA handlers set _actionFired first so they
    // aren't double-counted as dismissals.
    if (!_actionFired) {
      _actionFired = true;
      _fireSuccessAction('dismissed');
    }
    _unbind();
    _mountEl = null;
  }

  function onDestroy() {
    _unbind();
    _mountEl = null;
    _snapshot = null;
  }

  // ---------------------------------------------------------------------------
  // Render (Stage 2 — single-product placeholder; Stage 3 fills the receipt)
  // ---------------------------------------------------------------------------

  function _render(snap) {
    snap = snap || {};
    var isBundle = !!snap.isBundle;
    var product = snap.product || {};
    var titleText = _t(isBundle ? 'customizer.success.title_bundle' : 'customizer.success.title');
    // Product-card meta line: single uses the pre-built snapshot string; bundle
    // builds "{n} items · {distinct-logo count}" with locale pluralization (D3).
    var metaLine = isBundle ? _buildBundleCardMeta(product) : (product.metaLine || '');

    // Branding section. Single → one chip line per position. Bundle → one row
    // per garment type with stacked position·method·logo meta (or "No branding"),
    // under a "{branded} of {total} garment types" label (D2).
    var sectionHtml;
    if (isBundle) {
      var bundleLabel = _t('customizer.success.branding_label_bundle', {
        branded: snap.brandedCount || 0,
        total: snap.totalTypes || 0
      });
      sectionHtml = '<p class="jrs-success__sec-label">' + _escape(bundleLabel) + '</p>' +
        _renderBundleRows(snap.bundleRows || []);
    } else {
      var brandingHtml = _renderBrandingLines(snap.brandingLines || []);
      sectionHtml = brandingHtml
        ? '<p class="jrs-success__sec-label">' + _escape(_t('customizer.success.branding_label')) + '</p>' + brandingHtml
        : '';
    }

    // role="status" on the BODY only (mockup) — the receipt is announced; the
    // footer CTAs sit outside the live region.
    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--success" data-jrs-step="success">' +
      '  <div class="jrs-success">' +
      '    <div class="jrs-success__body" role="status">' +
      '      <div class="jrs-success__head">' +
      '        <span class="jrs-success__badge" aria-hidden="true">' + _checkSvg() + '</span>' +
      '        <h2 class="jrs-success__title" tabindex="-1">' + _escape(titleText) + '</h2>' +
      '      </div>' +
             _renderProductCard(product, metaLine) +
             sectionHtml +
      '    </div>' +
      '    <footer class="jrs-success__footer">' +
             // B2 (run-2 relaunch fix) — price breakdown IN THE PINNED FOOTER,
             // directly above the Checkout CTA. The footer is flex:none outside
             // the scrolling body, so the totals are on screen whenever the
             // Checkout button is — at 360×640 and every other viewport — and
             // Checkout is never presented before the VAT-inclusive total.
             // Wording mirrors the cart page's summary ("Product Costs" /
             // "Costs To Add Logo" / "VAT"); values fill from the single
             // authoritative getCart() read in _loadCartMeta (decision 2 / D3).
             _renderTotalsBlock(snap) +
      '      <button type="button" class="jrs-success__cta-outline jrs-success__cta-outline--primary" data-jrs-success-view-cart>' +
               _escape(_t('customizer.success.cta_view_cart')) +
      '      </button>' +
      '      <button type="button" class="jrs-success__cta-secondary" data-jrs-success-continue>' +
               _escape(_t('customizer.success.cta_continue')) +
      '      </button>' +
      '    </footer>' +
      '  </div>' +
      '</div>';
  }

  /**
   * B2 — the footer price-breakdown block. Rendered with "—" placeholders so
   * the row labels (and the logo-fee explanation they carry) are visible
   * immediately; _applyCartMeta fills the amounts from the authoritative
   * getCart() read (≤3s). The logo row is pre-hidden only for an unbranded
   * add (no logo lines can exist); otherwise it renders so the customer sees
   * the "Costs To Add Logo" wording the cart page uses.
   */
  function _renderTotalsBlock(snap) {
    var unbranded = !!(snap && snap.unbranded);
    return (
      // 2026-09-02 additions (fifth amendment): "YOUR CART · N ITEMS" eyebrow
      // (same style as the BRANDING SUMMARY label) — also the fix for the
      // "cost breakdown totals the whole cart but reads as the bundle" defect:
      // the totals are cart-wide and now say so.
      '<p class="jrs-success__sec-label" data-jrs-success-cart-label>' +
        'Your cart <span data-jrs-success-cart-count></span></p>' +
      '<dl class="jrs-success__totals" data-jrs-success-totals>' +
      '  <div class="jrs-success__totals-row">' +
      '    <dt>' + _escape(_t('customizer.success.price_products')) + '</dt>' +
      '    <dd data-price-wrapper>' +
      '      <span class="in-vat hidden" data-jrs-success-totals-products-inc>&mdash;</span>' +
      '      <span class="ex-vat hidden" data-jrs-success-totals-products-ex>&mdash;</span>' +
      '    </dd>' +
      '  </div>' +
      '  <div class="jrs-success__totals-row" data-jrs-success-totals-logo-row' + (unbranded ? ' hidden' : '') + '>' +
      '    <dt>' +
      // The summary row IS the toggle (sixth amendment): a real <button>,
      // aria-expanded/aria-controls, keyboard operable, chevron rotates.
      '      <button type="button" class="jrs-success__breakdown-toggle"' +
      '              data-jrs-success-breakdown-toggle' +
      '              aria-expanded="false" aria-controls="jrs-success-logo-breakdown">' +
               _escape(_t('customizer.success.price_logos')) +
      // Affordance label — swaps with aria-expanded; same button, ONE control.
      '        <span class="jrs-success__breakdown-affordance" data-jrs-success-breakdown-affordance>See breakdown</span>' +
      '        <svg class="jrs-success__breakdown-chevron" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 4.5 L6 8 L9.5 4.5"/></svg>' +
      '        <span class="jrs-success__breakdown-free-hint" data-jrs-success-free-hint hidden></span>' +
      '      </button>' +
      '    </dt>' +
      '    <dd data-price-wrapper>' +
      '      <span class="in-vat hidden" data-jrs-success-totals-logos-inc>&mdash;</span>' +
      '      <span class="ex-vat hidden" data-jrs-success-totals-logos-ex>&mdash;</span>' +
      '    </dd>' +
      '  </div>' +
      // Logo cost breakdown — grouped position+method rows, filled from the
      // authoritative getCart() read. £0 rows render "FREE" (operator ruling:
      // never "£0.00" — Left Breast is free on branded bundles and that is
      // the offer, not a broken calculation).
      '  <div class="jrs-success__logo-breakdown" id="jrs-success-logo-breakdown" data-jrs-success-logo-breakdown hidden></div>' +
      // Bulk buying discount = cart.total_discount (2026-09-01 reversion —
      // compare-at construction stays withdrawn). Hidden entirely at zero.
      '  <div class="jrs-success__totals-row jrs-success__totals-row--discount" data-jrs-success-totals-bulk-row hidden>' +
      '    <dt>Bulk buying discount</dt>' +
      '    <dd data-price-wrapper>' +
      '      <span class="in-vat hidden" data-jrs-success-totals-bulk-inc>&mdash;</span>' +
      '      <span class="ex-vat hidden" data-jrs-success-totals-bulk-ex>&mdash;</span>' +
      '    </dd>' +
      '  </div>' +
      '  <div class="jrs-success__totals-row">' +
      '    <dt>' + _escape(_t('customizer.success.price_vat')) + '</dt>' +
      '    <dd data-jrs-success-totals-vat>&mdash;</dd>' +
      '  </div>' +
      // Order total is inc. VAT and deliberately NOT toggle-wired (cart-summary
      // governed rule) — single value, no data-price-wrapper.
      '  <div class="jrs-success__totals-row jrs-success__totals-row--total">' +
      '    <dt>' + _escape(_t('customizer.success.price_total')) +
      '      <span class="jrs-success__totals-note">' + _escape(_t('customizer.success.price_total_note')) + '</span>' +
      '    </dt>' +
      '    <dd data-jrs-success-totals-total>&mdash;</dd>' +
      '  </div>' +
      '</dl>' +
      // Free-delivery line — BOTH states; governed dual form; toggle-aware amount.
      '<p class="jrs-success__delivery" data-jrs-success-delivery hidden>' +
      '  <span data-jrs-success-delivery-unlocked hidden>' +
      '    <span class="jrs-success__delivery-tick" aria-hidden="true">&#10003;</span> ' +
      '    You&rsquo;ve unlocked free UK delivery' +
      '  </span>' +
      '  <span data-jrs-success-delivery-togo hidden>' +
      '    <span data-price-wrapper class="jrs-success__delivery-amount">' +
      '      <span class="in-vat hidden" data-jrs-success-togo-inc>&mdash;</span>' +
      '      <span class="ex-vat hidden" data-jrs-success-togo-ex>&mdash;</span>' +
      '    </span>' +
      '    to go for free UK delivery &mdash; free over &pound;150 inc. VAT (&pound;125 ex.)' +
      '  </span>' +
      '</p>'
    );
  }

  function _renderProductCard(product, metaLine) {
    var name = product.title || '';
    var meta = metaLine || '';
    var price = product.priceLabel || '';
    return (
      '<div class="jrs-success__product">' +
      '  <span class="jrs-success__thumb" aria-hidden="true">' + _renderThumb(product.imageUrl) + '</span>' +
      '  <span class="jrs-success__product-text">' +
      '    <span class="jrs-success__product-name" title="' + _escape(name) + '">' + _escape(name) + '</span>' +
           (meta ? '<span class="jrs-success__product-meta">' + _escape(meta) + '</span>' : '') +
           (price ? '<span class="jrs-success__product-price">' + _escape(price) + '</span>' : '') +
      '  </span>' +
      '</div>'
    );
  }

  // --- Bundle receipt (Stage 4) ----------------------------------------------

  /** "{n} items · {distinct-logo count}" with one/other locale pluralization. */
  function _buildBundleCardMeta(product) {
    var n = product.itemCount || 0;
    var logos = product.distinctLogoCount || 0;
    var phrase = _t(
      logos === 1 ? 'customizer.success.bundle_logo_count_one'
                  : 'customizer.success.bundle_logo_count_other',
      { count: logos }
    );
    return _t('customizer.success.bundle_card_meta', { count: n, logos: phrase });
  }

  function _renderBundleRows(rows) {
    if (!rows || !rows.length) return '';
    var html = '';
    for (var i = 0; i < rows.length; i++) html += _renderBundleRow(rows[i]);
    return html;
  }

  function _renderBundleRow(row) {
    if (!row) return '';
    var metaHtml;
    if (row.branded && row.lines && row.lines.length) {
      var lines = '';
      for (var i = 0; i < row.lines.length; i++) lines += _renderBundleRowLine(row.lines[i]);
      metaHtml = lines;
    } else {
      // Unbranded type (D2) — excluded from the branded count upstream.
      metaHtml = '<span class="jrs-success__brow-meta jrs-success__brow-meta--none">' +
        _escape(_t('customizer.success.no_branding')) + '</span>';
    }
    return (
      '<div class="jrs-success__brow">' +
      '  <span class="jrs-success__brow-thumb" aria-hidden="true">' + _renderThumb(row.imageUrl) + '</span>' +
      '  <span class="jrs-success__brow-text">' +
      '    <span class="jrs-success__brow-name" title="' + _escape(row.name || '') + '">' +
             _escape(row.name || '') +
      '    </span>' +
           metaHtml +
      '  </span>' +
      '  <span class="jrs-success__brow-qty">×' + (parseInt(row.qty, 10) || 0) + '</span>' +
      '</div>'
    );
  }

  /** One stacked "position · method · logo" meta line (ellipsis + title attr). */
  function _renderBundleRowLine(line) {
    if (!line) return '';
    var parts = [];
    if (line.positionLabel) parts.push(_escape(line.positionLabel));
    if (line.methodLabel) parts.push(_escape(line.methodLabel));
    if (line.logo && line.logo.label) parts.push(_escape(line.logo.label));
    var full = parts.join(' · ');
    return '<span class="jrs-success__brow-meta" title="' + full + '">' + full + '</span>';
  }

  function _renderThumb(imageUrl) {
    if (imageUrl) {
      return '<img class="jrs-success__thumb-img" src="' + _escape(imageUrl) + '" alt="" loading="lazy">';
    }
    // Garment placeholder when no featured image (D4) — mirrors review's card.
    return '<svg class="jrs-success__thumb-fallback" viewBox="0 0 88 100" ' +
      'xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M22 8 L14 16 L14 30 L22 30 L22 92 L66 92 L66 30 L74 30 L74 16 L66 8 L52 8 ' +
      'C52 13 36 13 36 8 Z" fill="none" stroke="currentColor" stroke-width="3" stroke-linejoin="round"/>' +
      '<path d="M38 8 L44 18 L50 8" fill="none" stroke="currentColor" stroke-width="3" stroke-linejoin="round"/>' +
      '</svg>';
  }

  function _renderBrandingLines(lines) {
    if (!lines || !lines.length) return '';
    var html = '';
    for (var i = 0; i < lines.length; i++) {
      html += _renderBrandingLine(lines[i]);
    }
    return html;
  }

  function _renderBrandingLine(line) {
    if (!line) return '';
    var pos = line.positionLabel || '';
    var method = line.methodLabel || '';
    var chip = '';
    if (line.logo && line.logo.label) {
      var miniKind = line.logo.type === 'text' ? 'text' : 'file';
      // title attr carries the full value so a truncated chip is hoverable (D4).
      chip =
        '<span class="jrs-success__chip" title="' + _escape(line.logo.label) + '">' +
        '<span class="jrs-success__chip-mini jrs-success__chip-mini--' + miniKind + '">' +
          _escape(line.logo.mini || '') +
        '</span>' +
        '<span class="jrs-success__chip-label">' + _escape(line.logo.label) + '</span>' +
        '</span>';
    }
    return (
      '<div class="jrs-success__line">' +
      '  <span class="jrs-success__pos-dot" aria-hidden="true"></span>' +
      '  <span class="jrs-success__what" title="' + _escape(pos) + '">' + _escape(pos) + '</span>' +
         (method
           ? '<span class="jrs-success__sep" aria-hidden="true">·</span>' +
             '<span class="jrs-success__method">' + _escape(method) + '</span>'
           : '') +
         chip +
      '</div>'
    );
  }

  function _focusHeading() {
    if (!_mountEl) return;
    var h = _mountEl.querySelector('.jrs-success__title');
    if (h && typeof h.focus === 'function') {
      try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
    }
  }

  // ---------------------------------------------------------------------------
  // Interactions (CTAs — Doc 6 §13.4 sanctioned close exception)
  // ---------------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;
    _bind(_mountEl.querySelector('[data-jrs-success-view-cart]'), 'click', _onViewCart);
    _bind(_mountEl.querySelector('[data-jrs-success-continue]'), 'click', _onContinue);
    _bind(_mountEl.querySelector('[data-jrs-success-breakdown-toggle]'), 'click', _onBreakdownToggle);
  }

  function _onCheckout(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (_actionFired) return; // double-click guard (FR9 / §6)
    _actionFired = true;
    // Deliver the action BEFORE navigating, via the nav-safe beacon (mirrors
    // _onViewCart — the bundle cart-validation already ran at add-time, written
    // to the cart attributes, so going straight to /checkout skips no validation).
    _fireSuccessAction('checkout', true);
    // Close-as-confirmed FIRST (same rationale as _onViewCart): unbinds the
    // shell's pagehide handler + flips state.isOpen=false so the checkout
    // navigation emits no abandon event; the terminal close also clears the
    // draft (decision 4) so the purchased config is never resumable.
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.close === 'function') {
      JRS.CustomizerPopup.close({ reason: 'checkout', isConfirm: true });
    } else if (JRS.CustomizerPopupState &&
               typeof JRS.CustomizerPopupState.clearStorage === 'function') {
      JRS.CustomizerPopupState.clearStorage();
    }
    window.location.href = '/checkout';
  }

  function _onViewCart(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (_actionFired) return; // double-click guard (FR9 / §6)
    _actionFired = true;
    // FR9 — deliver view_cart BEFORE the navigation, via the nav-safe beacon
    // (sendBeacon survives unload). _actionFired is already set, so the
    // close-driven onExit below won't double-count this as a 'dismissed'.
    _fireSuccessAction('view_cart', true);
    // D2 — the shell binds a `pagehide` handler (_bindOpenListeners) that fires
    // pdp_customizer_abandon on navigation WHILE the popup is open in state
    // (_handlePageHide, guarded by _isOpenInState). View cart navigates with the
    // popup still open, so we MUST synchronously close-as-confirmed FIRST:
    // close({isConfirm:true}) (a) unbinds the pagehide listener via
    // _unbindOpenListeners and (b) flips state.isOpen=false — either alone
    // suppresses the navigation abandon, so confirm→success→View cart emits
    // zero abandon events. The terminal close path also dispatches RESET →
    // clearStorage (decision 4), so the purchased config is never resumable.
    // Only THEN navigate.
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.close === 'function') {
      JRS.CustomizerPopup.close({ reason: 'view_cart', isConfirm: true });
    } else if (JRS.CustomizerPopupState &&
               typeof JRS.CustomizerPopupState.clearStorage === 'function') {
      // Fallback if the shell close is somehow unavailable: at least clear the
      // draft so the purchased config isn't offered for resume.
      JRS.CustomizerPopupState.clearStorage();
    }
    window.location.href = '/cart';
  }

  function _onContinue(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (_actionFired) return;
    _actionFired = true;
    // nav-safe beacon — we reload the PDP below, so the event must survive unload.
    _fireSuccessAction('continue_shopping', true);
    // Clear THIS product's saved selection so the fresh PDP has nothing to
    // resume (the customizer draft is cleared by the terminal close below).
    if (JRS.PdpSelectionStore && typeof JRS.PdpSelectionStore.clear === 'function') {
      try { JRS.PdpSelectionStore.clear(); } catch (err) {}
    }
    // isConfirm:true suppresses the abandon event + save-on-close prompt (FR5);
    // the shell's terminal-close handling also clears the draft (decision 4).
    if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.close === 'function') {
      JRS.CustomizerPopup.close({ reason: 'continue_shopping', isConfirm: true });
    }
    // Give the shopper a FRESH PDP — the configured items are committed to
    // cart, so the on-page builder must not linger with the old selection.
    // Preferred (2026-08-01): reset the multistep form IN PLACE via its
    // public reset() (back to step 1, colour + quantities cleared) — no
    // reload, no lost scroll/gallery state, instant. Fallback: surfaces
    // without a resettable form (bundle PDP builder, legacy PDP) still get
    // the old full reload for a guaranteed clean slate. Guarded so
    // restricted/test environments (jsdom) don't throw.
    var resetInPlace = false;
    try {
      var pdpForm = document.querySelector('pdp-multistep-form-v2');
      if (pdpForm && typeof pdpForm.reset === 'function') {
        resetInPlace = pdpForm.reset() === true;
      }
    } catch (err) { /* fall through to reload */ }
    if (!resetInPlace) {
      try {
        if (window.location && typeof window.location.reload === 'function') {
          window.location.reload();
        }
      } catch (err) { /* no-op */ }
    }
  }

  /**
   * Fire pdp_customizer_success_action (FR9). The shell envelope adds
   * popup_session_id / device_class / popup_version 'v3'; is_bundle is taken
   * from the frozen snapshot.
   *
   * Transport (Stage 6 closeout gate): dataLayer-FIRST. The sendBeacon endpoint
   * /apps/jrs-popup-events is UNPROVISIONED — it returns 404 (the provisioned
   * App Proxy is apps/jrs-logos; see the Doc 3 v0.3 gap note + the Stage 6
   * closeout). So the GA4/GTM (dataLayer) path is the transport that actually
   * delivers today. For a nav-bound action (view_cart) we push to the dataLayer
   * BEFORE navigating, then ALSO fire the beacon best-effort so delivery
   * upgrades for free if the endpoint is ever provisioned. GA4 only ingests the
   * dataLayer copy (the beacon 404s), so there is no double-count.
   * @param {'checkout'|'view_cart'|'continue_shopping'|'dismissed'} action
   * @param {boolean} [navSafe] - true for checkout/view_cart (also send the best-effort beacon)
   */
  function _fireSuccessAction(action, navSafe) {
    if (!JRS.CustomizerPopup) return;
    var fields = { action: action, is_bundle: !!(_snapshot && _snapshot.isBundle) };
    if (typeof JRS.CustomizerPopup.fireAnalytics === 'function') {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_success_action', fields);
    }
    if (navSafe && typeof JRS.CustomizerPopup.fireAnalyticsBeacon === 'function') {
      JRS.CustomizerPopup.fireAnalyticsBeacon('pdp_customizer_success_action', fields);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function _t(key, params) {
    return (JRS.i18n && typeof JRS.i18n.t === 'function') ? JRS.i18n.t(key, params) : key;
  }

  function _bind(el, type, handler) {
    if (!el || !el.addEventListener) return;
    el.addEventListener(type, handler);
    _bound.push({ el: el, type: type, handler: handler });
  }

  function _unbind() {
    for (var i = 0; i < _bound.length; i++) {
      var b = _bound[i];
      if (b.el && b.el.removeEventListener) b.el.removeEventListener(b.type, b.handler);
    }
    _bound = [];
  }

  function _escape(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function _checkSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M20 6 9 17l-5-5"></path></svg>';
  }

  // ---------------------------------------------------------------------------
  // Cart meta (D3 / decision 2) — CartAPI.getCart() is the single authoritative
  // read after the add. No timeout: getCart always returns the CURRENT cart, so
  // a late fill can never show a stale number — the old 3s race-and-drop turned
  // a slow /cart.js (routine right after a multi-product sequential add) into a
  // receipt stuck on em-dashes forever. Transient errors retry twice; the mount
  // identity guard still drops results from a closed/re-entered session.
  // ---------------------------------------------------------------------------

  function _loadCartMeta() {
    if (!JRS.CartAPI || typeof JRS.CartAPI.getCart !== 'function') return;
    var mountAtCall = _mountEl; // identity guard: a close/re-entry invalidates

    function attempt(retriesLeft) {
      JRS.CartAPI.getCart().then(function (cart) {
        if (_mountEl !== mountAtCall || !_mountEl) return; // stale render — drop
        _applyCartMeta(cart);
      })['catch'](function () {
        if (_mountEl !== mountAtCall || !_mountEl) return;
        if (retriesLeft > 0) {
          setTimeout(function () { attempt(retriesLeft - 1); }, CART_META_RETRY_MS);
        }
        // out of retries → the breakdown keeps its placeholders
      });
    }
    attempt(2);
  }

  function _applyCartMeta(cart) {
    // Authoritative shape = Shopify /cart.js. null/timeout/malformed → the
    // breakdown keeps its "—" placeholders (labels stay visible; never show a
    // stale or wrong number).
    if (!cart || typeof cart.item_count !== 'number' || typeof cart.total_price !== 'number') {
      return;
    }
    var totals = _computeCartTotals(cart);
    if (!totals) return;
    _setTotalsPair('products', totals.productCosts);
    // Operator ruling: a zero-cost logo total renders "FREE", never "£0.00" —
    // seen on all-free bundles (free Left Breast × every garment type + free
    // Beanie Front). Both VAT-pair spans read FREE so the toggle stays inert.
    if (totals.logoLines > 0 && totals.logoTotal === 0) {
      var lgEx = _mountEl.querySelector('[data-jrs-success-totals-logos-ex]');
      var lgInc = _mountEl.querySelector('[data-jrs-success-totals-logos-inc]');
      if (lgEx) { lgEx.textContent = 'FREE'; lgEx.classList.add('jrs-success__logo-breakdown-free'); }
      if (lgInc) { lgInc.textContent = 'FREE'; lgInc.classList.add('jrs-success__logo-breakdown-free'); }
    } else {
      _setTotalsPair('logos', totals.logoTotal);
      var lgEx2 = _mountEl.querySelector('[data-jrs-success-totals-logos-ex]');
      var lgInc2 = _mountEl.querySelector('[data-jrs-success-totals-logos-inc]');
      if (lgEx2) lgEx2.classList.remove('jrs-success__logo-breakdown-free');
      if (lgInc2) lgInc2.classList.remove('jrs-success__logo-breakdown-free');
    }
    _setTotalsValue('[data-jrs-success-totals-vat]', totals.vat);
    _setTotalsValue('[data-jrs-success-totals-total]', totals.total);
    var logoRow = _mountEl.querySelector('[data-jrs-success-totals-logo-row]');
    if (logoRow) {
      // Mirror the cart page: the "Costs To Add Logo" row shows only when
      // there are logo/branding lines in the cart. The logo lines show even
      // when their TOTAL is £0 (all-free positions) — the itemised rows
      // render "FREE", which is the offer.
      if (totals.logoLines > 0) logoRow.removeAttribute('hidden');
      else logoRow.setAttribute('hidden', '');
    }
    // "YOUR CART · N ITEMS" count.
    var countEl = _mountEl.querySelector('[data-jrs-success-cart-count]');
    if (countEl) {
      countEl.textContent = '· ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }
    // Logo cost breakdown — grouped "N × Position · Method" rows; FREE at £0.
    // COLLAPSED by default (sixth amendment): rows are filled while hidden —
    // every amount cell carries data-price-wrapper, so the VAT toggle reaches
    // them while collapsed and they are correct when opened.
    var bd = _mountEl.querySelector('[data-jrs-success-logo-breakdown]');
    _hasBreakdownRows = !!(totals.logoGroups && totals.logoGroups.length);
    // Optional FREE-in-summary hint (operator to decide; off by default):
    // surfaces the free position in the collapsed summary row.
    var hint = _mountEl.querySelector('[data-jrs-success-free-hint]');
    if (hint) {
      var freeGrp = null;
      for (var fg = 0; fg < totals.logoGroups.length; fg++) {
        if (totals.logoGroups[fg].pence === 0) { freeGrp = totals.logoGroups[fg]; break; }
      }
      if (freeGrp && window.JRS_SUCCESS_FREE_SUMMARY === true) {
        hint.textContent = '· ' + freeGrp.label.split(' · ')[0] + ' free';
        hint.removeAttribute('hidden');
      } else {
        hint.textContent = '';
        hint.setAttribute('hidden', '');
      }
    }
    if (bd) {
      if (totals.logoGroups.length) {
        var rows = '';
        for (var g = 0; g < totals.logoGroups.length; g++) {
          var grp = totals.logoGroups[g];
          rows +=
            '<div class="jrs-success__logo-breakdown-row">' +
            '  <span class="jrs-success__logo-breakdown-label">' +
                 grp.count + ' × ' + _escape(grp.label) + '</span>' +
            (grp.pence > 0
              ? '<span data-price-wrapper class="jrs-success__logo-breakdown-amt">' +
                '<span class="in-vat hidden">£' + _formatGBP(Math.round(grp.pence * 1.2)) + '</span>' +
                '<span class="ex-vat hidden">£' + _formatGBP(grp.pence) + '</span></span>'
              : '<span class="jrs-success__logo-breakdown-free">FREE</span>') +
            '</div>';
        }
        bd.innerHTML = rows;
        if (_breakdownExpanded) bd.removeAttribute('hidden');
        else bd.setAttribute('hidden', '');
      } else {
        bd.innerHTML = '';
        bd.setAttribute('hidden', '');
      }
    }
    // Bulk buying discount — cart.total_discount, hidden entirely at zero.
    var bulkRow = _mountEl.querySelector('[data-jrs-success-totals-bulk-row]');
    if (bulkRow) {
      if (totals.bulk > 0) {
        _setTotalsPair('bulk', totals.bulk, '-');
        bulkRow.removeAttribute('hidden');
      } else {
        bulkRow.setAttribute('hidden', '');
      }
    }
    // Free-delivery line — both states, off the discounted ex-VAT total.
    var dWrap = _mountEl.querySelector('[data-jrs-success-delivery]');
    var dOk = _mountEl.querySelector('[data-jrs-success-delivery-unlocked]');
    var dTogo = _mountEl.querySelector('[data-jrs-success-delivery-togo]');
    if (dWrap && dOk && dTogo) {
      dWrap.removeAttribute('hidden');
      if (totals.netEx >= 12500) {
        dOk.removeAttribute('hidden');
        dTogo.setAttribute('hidden', '');
      } else {
        var remain = 12500 - totals.netEx;
        var incEl = _mountEl.querySelector('[data-jrs-success-togo-inc]');
        var exEl = _mountEl.querySelector('[data-jrs-success-togo-ex]');
        if (incEl) incEl.textContent = '£' + _formatGBP(Math.round(remain * 1.2)) + ' inc. VAT';
        if (exEl) exEl.textContent = '£' + _formatGBP(remain) + ' ex. VAT';
        dTogo.removeAttribute('hidden');
        dOk.setAttribute('hidden', '');
      }
    }
    _applyVatToggleState();
  }

  /** Write an ex/inc pair (inc = ex × 1.2, the cart-summary construction). */
  function _setTotalsPair(key, exPence, sign) {
    sign = sign || '';
    var ex = _mountEl && _mountEl.querySelector('[data-jrs-success-totals-' + key + '-ex]');
    var inc = _mountEl && _mountEl.querySelector('[data-jrs-success-totals-' + key + '-inc]');
    if (ex) ex.textContent = sign + '£' + _formatGBP(exPence) + ' ex. VAT';
    if (inc) inc.textContent = sign + '£' + _formatGBP(Math.round(exPence * 1.2)) + ' inc. VAT';
  }

  /** Apply the sitewide VAT-toggle state (betterconversions-addon setPrice
      semantics: sessionStorage exclVATv3 'true' → show .ex-vat) to the
      receipt's freshly rendered pairs. Later toggle flips reach them through
      the addon itself because every pair cell carries data-price-wrapper. */
  function _applyVatToggleState() {
    if (!_mountEl) return;
    var exVat = true;
    try { exVat = sessionStorage.getItem('exclVATv3') !== 'false'; } catch (e) {}
    var wrappers = _mountEl.querySelectorAll('[data-price-wrapper]');
    for (var i = 0; i < wrappers.length; i++) {
      var inEls = wrappers[i].querySelectorAll('.in-vat');
      var exEls = wrappers[i].querySelectorAll('.ex-vat');
      var j;
      for (j = 0; j < inEls.length; j++) inEls[j].classList[exVat ? 'add' : 'remove']('hidden');
      for (j = 0; j < exEls.length; j++) exEls[j].classList[exVat ? 'remove' : 'add']('hidden');
    }
  }

  function _setTotalsValue(selector, pence) {
    var el = _mountEl && _mountEl.querySelector(selector);
    if (el) el.textContent = '£' + _formatGBP(pence);
  }

  /**
   * B2 — whole-cart breakdown, computed EXACTLY like the cart page's
   * `_computeTotalsFromCart` (jrs-v2-cart-page.js): line price from
   * final_line_price (fallback line_price); logo/branding lines identified by
   * the customization-position product type OR the _ParentVariant child
   * marker; VAT = 20% of the taxable subtotal only (VAT-free items carry
   * taxable:false in /cart.js). All pence.
   *
   * @param {Object} cart - /cart.js response
   * @returns {{productCosts:number, logoTotal:number, vat:number, total:number}|null}
   */
  function _computeCartTotals(cart) {
    if (!cart || !cart.items || !cart.items.length) return null;
    var subtotal = 0;
    var taxableSubtotal = 0;
    var logoTotal = 0;
    var logoLines = 0;
    var groups = {};
    var groupOrder = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i] || {};
      // GROSS line price (2026-09-02 gate fix, third failure of this column):
      // rows and breakdown groups are built from original_line_price so the
      // construction is correct for BOTH discount allocation styles —
      //   cart-level (garment ladder): original == final, total_discount is
      //     the cart-level amount;
      //   line-level (position bands): original is gross, final is net, and
      //     Shopify's total_discount includes the line allocations.
      // Either way: Products(orig) + Logo(orig) − cart.total_discount =
      // cart.total_price, by Shopify's own identity. Do NOT special-case the
      // two styles — special-casing is how this column broke three times.
      var linePrice = Number(
        typeof item.original_line_price !== 'undefined' ? item.original_line_price
          : (typeof item.final_line_price !== 'undefined' ? item.final_line_price : item.line_price)
      );
      if (!isFinite(linePrice) || linePrice < 0) linePrice = 0;
      subtotal += linePrice;
      var props = item.properties || {};
      var isLogoLine = item.product_type === 'customization-position' ||
        (props._ParentVariant != null && props._ParentVariant !== '');
      if (isLogoLine) {
        logoTotal += linePrice;
        logoLines++;
        // Breakdown grouping: identical position+method combinations merge,
        // counts sum. Display name strips the pricing-product prefixes
        // ("Free Left Breast" / "Bundle Large Back" → the position itself).
        var posName = String(item.product_title || item.title || '')
          .replace(/^\s*(Free|Bundle)\s+/i, '').replace(/\s*-\s*(Embroidery|Print)\s*$/i, '');
        var method = String(item.variant_title || '');
        var key = posName.toLowerCase() + '|' + method.toLowerCase() + '|' + (linePrice > 0 ? 'paid' : 'free');
        if (!groups[key]) {
          groups[key] = { label: posName + ' · ' + method, count: 0, pence: 0 };
          groupOrder.push(key);
        }
        groups[key].count += (Number(item.quantity) || 1);
        groups[key].pence += linePrice;
      }
      var isTaxable = typeof item.taxable === 'boolean' ? item.taxable : true;
      if (isTaxable) taxableSubtotal += linePrice;
    }
    // Governing arithmetic (fifth amendment, mirrors the cart-summary
    // construction — this column has been wrong twice before):
    //   Products(gross) + Logo(gross) − cart.total_discount = net
    //   VAT = 20% of the NET taxable figure
    //   Order total = cart.total_price + VAT, always inc. VAT.
    // final_line_price sums are gross here (the bulk discount is cart-level);
    // cart.total_price is the authoritative net.
    var bulk = (typeof cart.total_discount === 'number' && cart.total_discount > 0)
      ? cart.total_discount : 0;
    var netEx = (typeof cart.total_price === 'number') ? cart.total_price : (subtotal - bulk);
    var nonTaxable = subtotal - taxableSubtotal;
    var netTaxable = netEx - nonTaxable;
    if (!isFinite(netTaxable) || netTaxable < 0) netTaxable = 0;
    var vat = Math.round(netTaxable * 0.2);
    var productCosts = subtotal - logoTotal;
    if (!isFinite(productCosts) || productCosts < 0) productCosts = subtotal;
    var logoGroups = [];
    for (var gI = 0; gI < groupOrder.length; gI++) logoGroups.push(groups[groupOrder[gI]]);
    return {
      productCosts: productCosts,
      logoTotal: logoTotal,
      logoLines: logoLines,
      logoGroups: logoGroups,
      bulk: bulk,
      netEx: netEx,
      vat: vat,
      total: netEx + vat
    };
  }

  /** Pence → "X.XX" (GBP; the £ symbol is supplied by the copy template). */
  function _formatGBP(pence) {
    var n = (typeof pence === 'number' ? pence : 0) / 100;
    return n.toFixed(2);
  }

  // ---------------------------------------------------------------------------
  // Public API + registration
  // ---------------------------------------------------------------------------

  JRS.CustomizerSuccessStep = {
    STEP_ID: STEP_ID,
    prepare: prepare,
    onEnter: onEnter,
    onExit: onExit,
    onDestroy: onDestroy,
    // Test seams — NOT day-to-day API (underscore prefix, per JRS convention).
    // success-close.test.js asserts the CTAs request an isConfirm close;
    // success-snapshot.test.js exercises the GBP formatter / cart-meta apply.
    _onCheckout: _onCheckout,
    _onViewCart: _onViewCart,
    _onContinue: _onContinue,
    _formatGBP: _formatGBP,
    _applyCartMeta: _applyCartMeta,
    _computeCartTotals: _computeCartTotals
  };
  // Mirror onto JRS.Step for parity with the other step controllers.
  JRS.Step.Success = JRS.CustomizerSuccessStep;

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter, onExit: onExit, onDestroy: onDestroy, terminal: true
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onDestroy: onDestroy, terminal: true }
      });
    }
  }
  _register();
})(window.JRS);
